// =============================================================================
// MeuDriver — SkillRadarChart
// Gráfico radar das 10 habilidades de direção do aluno (usa Recharts)
// =============================================================================

import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'
import { Skeleton } from '@/components/ui/skeleton'
import type { StudentSkillAverages } from '@/types/database'

const SKILL_LABELS: Record<string, string> = {
  avg_partida:       'Partida',
  avg_direcao:       'Direção',
  avg_freios:        'Freios',
  avg_marchas:       'Marchas',
  avg_baliza:        'Baliza',
  avg_garagem:       'Garagem',
  avg_retorno:       'Retorno',
  avg_via_rapida:    'Via Rápida',
  avg_noturna:       'Noturna',
  avg_comportamento: 'Comportamento',
}

const SKILL_KEYS = Object.keys(SKILL_LABELS) as Array<keyof StudentSkillAverages>

interface Props {
  averages: StudentSkillAverages | null
  isLoading?: boolean
}

export function SkillRadarChart({ averages, isLoading }: Props) {
  if (isLoading) {
    return <Skeleton className="h-64 w-full rounded-2xl" />
  }

  if (!averages || averages.lessons_rated === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 rounded-2xl border border-dashed border-border text-center gap-2">
        <p className="text-sm font-medium text-gray-500">Sem avaliações de habilidades ainda</p>
        <p className="text-xs text-muted-foreground max-w-xs">
          Peça ao seu instrutor para avaliar suas habilidades após as aulas.
        </p>
      </div>
    )
  }

  const data = SKILL_KEYS.map((key) => ({
    skill: SKILL_LABELS[key],
    value: Number(averages[key as keyof StudentSkillAverages] ?? 0),
    fullMark: 5,
  }))

  return (
    <div className="w-full" data-testid="skill-radar-chart">
      <ResponsiveContainer width="100%" height={280}>
        <RadarChart data={data} margin={{ top: 10, right: 20, bottom: 10, left: 20 }}>
          <PolarGrid stroke="#e5e7eb" />
          <PolarAngleAxis
            dataKey="skill"
            tick={{ fontSize: 11, fill: '#6b7280' }}
          />
          <PolarRadiusAxis
            domain={[0, 5]}
            tickCount={6}
            tick={{ fontSize: 9, fill: '#9ca3af' }}
            axisLine={false}
          />
          <Radar
            name="Habilidade"
            dataKey="value"
            stroke="#0054b3"
            fill="#0054b3"
            fillOpacity={0.2}
            strokeWidth={2}
          />
          <Tooltip
            formatter={(value, _name, entry) => [
              `${Number(value).toFixed(1)} / 5`,
              // `payload` é opcional no tipo do recharts — o rótulo cai para
              // string vazia se o ponto não vier com o dado da habilidade.
              (entry?.payload as { skill?: string } | undefined)?.skill ?? '',
            ]}
            contentStyle={{
              borderRadius: '0.75rem',
              border: '1px solid #e5e7eb',
              fontSize: 12,
            }}
          />
        </RadarChart>
      </ResponsiveContainer>
      <p className="text-xs text-center text-muted-foreground mt-1">
        Baseado em {averages.lessons_rated} avaliação{averages.lessons_rated !== 1 ? 'ões' : ''} do instrutor
      </p>
    </div>
  )
}
